/**
 * MCP Servers Hook
 *
 * React hook for managing MCP server connections and state.
 * Handles connection lifecycle, tool discovery, and execution.
 */

"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { getMCPServers, getEnabledMCPServers, isMCPEnabled, updateLastConnected, type MCPServerConfig } from "@/lib/mcpStorage";
import { mcpClientManager, type MCPConnection, type MCPTool } from "@/lib/mcpClient";
type UseMCPServersResult = {
  servers: MCPServerConfig[];
  connections: MCPConnection[];
  tools: Array<MCPTool & {
    serverId: string;
    serverName: string;
  }>;
  isEnabled: boolean;
  isConnecting: boolean;
  connectServer: (serverId: string) => Promise<void>;
  disconnectServer: (serverId: string) => Promise<void>;
  connectAllEnabled: () => Promise<void>;
  disconnectAll: () => Promise<void>;
  callTool: (serverId: string, toolName: string, args: Record<string, any>) => Promise<any>;
  refreshServers: () => void;
  getServerStatus: (serverId: string) => "connected" | "disconnected" | "error" | "connecting";
};
export function useMCPServers(): UseMCPServersResult {
  const [servers, setServers] = useState<MCPServerConfig[]>([]);
  const [connections, setConnections] = useState<MCPConnection[]>([]);
  const [tools, setTools] = useState<Array<MCPTool & {
    serverId: string;
    serverName: string;
  }>>([]);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectingServers, setConnectingServers] = useState<Set<string>>(new Set());

  // Track if this instance should stay connected
  const shouldStayConnectedRef = useRef(true);
  const hasConnectedRef = useRef(false);

  // Load servers and enabled state
  const refreshServers = useCallback(() => {
    const loadedServers = getMCPServers();
    const mcpEnabled = isMCPEnabled();
    console.log('[MCP Debug] Servers loaded:', loadedServers.length, 'MCP Enabled:', mcpEnabled);
    setServers(loadedServers);
    setIsEnabled(mcpEnabled);
  }, []);
  useEffect(() => {
    refreshServers();
  }, [refreshServers]);

  // Update connections and tools when they change
  const refreshConnections = useCallback(() => {
    const allConnections = mcpClientManager.getAllConnections();
    setConnections(allConnections);
    const allTools = mcpClientManager.getAllTools();
    setTools(allTools);
  }, []);

  // Connect to a specific server
  const connectServer = useCallback(async (serverId: string) => {
    const server = getMCPServers().find(s => s.id === serverId);
    if (!server) {
      console.error(`Server ${serverId} not found`);
      return;
    }
    setConnectingServers(prev => new Set(prev).add(serverId));
    setIsConnecting(true);
    try {
      await mcpClientManager.connect(server);
      updateLastConnected(serverId);
      refreshConnections();
      console.log(`[MCP] Connected to server: ${server.name}`);
    } catch (error) {
      console.error(`[MCP] Failed to connect to server ${server.name}:`, error);
      refreshConnections(); // Update to show error state
    } finally {
      setConnectingServers(prev => {
        const next = new Set(prev);
        next.delete(serverId);
        return next;
      });
      setIsConnecting(false);
    }
  }, [refreshConnections]);

  // Disconnect from a specific server
  const disconnectServer = useCallback(async (serverId_0: string) => {
    try {
      await mcpClientManager.disconnect(serverId_0);
      refreshConnections();
      console.log(`[MCP] Disconnected from server: ${serverId_0}`);
    } catch (error_0) {
      console.error(`[MCP] Error disconnecting from server ${serverId_0}:`, error_0);
    }
  }, [refreshConnections]);

  // Connect to all enabled servers
  const connectAllEnabled = useCallback(async () => {
    if (!isMCPEnabled()) {
      console.log("[MCP] MCP feature is disabled");
      return;
    }
    const enabledServers = getEnabledMCPServers();
    if (enabledServers.length === 0) {
      console.log("[MCP] No enabled servers to connect");
      return;
    }
    setIsConnecting(true);
    console.log(`[MCP] Connecting to ${enabledServers.length} enabled servers...`);
    const connectPromises = enabledServers.map(server_0 => connectServer(server_0.id).catch(error_1 => {
      console.error(`[MCP] Failed to connect to ${server_0.name}:`, error_1);
    }));
    await Promise.all(connectPromises);
    setIsConnecting(false);
  }, [connectServer]);

  // Disconnect all servers
  const disconnectAll = useCallback(async () => {
    try {
      await mcpClientManager.disconnectAll();
      refreshConnections();
      console.log("[MCP] Disconnected from all servers");
    } catch (error_2) {
      console.error("[MCP] Error disconnecting from all servers:", error_2);
    }
  }, [refreshConnections]);

  // Call a tool on a specific server
  const callTool = useCallback(async (serverId_1: string, toolName: string, args: Record<string, any>) => {
    try {
      const result = await mcpClientManager.callTool(serverId_1, toolName, args);
      console.log(`[MCP] Tool ${toolName} called successfully on server ${serverId_1}`);
      return result;
    } catch (error_3) {
      console.error(`[MCP] Error calling tool ${toolName} on server ${serverId_1}:`, error_3);
      throw error_3;
    }
  }, []);

  // Get status of a specific server
  const getServerStatus = useCallback((serverId_2: string): "connected" | "disconnected" | "error" | "connecting" => {
    if (connectingServers.has(serverId_2)) {
      return "connecting";
    }
    const connection = connections.find(c => c.serverId === serverId_2);
    if (!connection) {
      return "disconnected";
    }
    return connection.status === "connected" ? "connected" : connection.status === "error" ? "error" : "disconnected";
  }, [connections, connectingServers]);

  // Auto-connect to enabled servers when MCP is enabled
  useEffect(() => {
    if (isEnabled && !hasConnectedRef.current) {
      console.log('[MCP] MCP enabled, auto-connecting to enabled servers...');
      shouldStayConnectedRef.current = true;
      const connect = async () => {
        if (!isMCPEnabled()) {
          console.log("[MCP] MCP feature is disabled");
          return;
        }
        const enabledServers_0 = getEnabledMCPServers();
        if (enabledServers_0.length === 0) {
          console.log("[MCP] No enabled servers to connect");
          return;
        }
        setIsConnecting(true);
        console.log(`[MCP] Connecting to ${enabledServers_0.length} enabled servers...`);
        for (const server_1 of enabledServers_0) {
          if (!shouldStayConnectedRef.current) break;
          try {
            await connectServer(server_1.id);
          } catch (error_4) {
            console.error(`[MCP] Failed to connect to ${server_1.name}:`, error_4);
          }
        }
        if (shouldStayConnectedRef.current) {
          setIsConnecting(false);
          hasConnectedRef.current = true;
        }
      };
      connect();
    }

    // Only disconnect if MCP is disabled
    if (!isEnabled && hasConnectedRef.current) {
      console.log('[MCP] MCP disabled, disconnecting...');
      shouldStayConnectedRef.current = false;
      disconnectAll();
      hasConnectedRef.current = false;
    }
  }, [isEnabled, connectServer, disconnectAll]); // Depend on isEnabled

  // Clean up stale connections when servers change
  useEffect(() => {
    const enabledServerIds = new Set(servers.filter(s_0 => s_0.enabled).map(s_1 => s_1.id));
    const connectedServerIds = new Set(connections.map(c_0 => c_0.serverId));

    // Disconnect servers that are no longer enabled or were removed
    for (const connectedId of connectedServerIds) {
      if (!enabledServerIds.has(connectedId)) {
        console.log(`[MCP] Disconnecting removed/disabled server: ${connectedId}`);
        disconnectServer(connectedId);
      }
    }

    // Refresh connections after cleanup
    refreshConnections();
  }, [servers, connections.length]); // Run when servers list changes

  // Cleanup only on true unmount
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    return () => {
      // Only disconnect if we're truly unmounting (not a React Strict Mode remount)
      timeoutId = setTimeout(() => {
        if (!shouldStayConnectedRef.current) {
          console.log('[MCP] Component truly unmounting, disconnecting...');
          disconnectAll();
        }
      }, 200);
    };
  }, [disconnectAll]);
  return {
    servers,
    connections,
    tools,
    isEnabled,
    isConnecting,
    connectServer,
    disconnectServer,
    connectAllEnabled,
    disconnectAll,
    callTool,
    refreshServers,
    getServerStatus
  };
}